import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../../core/utils/blog";

const DISPLAY_FONT = "'Anton', 'Oswald', 'Arial Narrow', sans-serif";

const FeaturedSlider = ({ blogs = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (blogs.length <= 1) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % blogs.length);
        requestAnimationFrame(() => { setIsTransitioning(false); });
      }, 350);
    }, 5000);

    return () => clearInterval(interval);
  }, [blogs.length]);

  const changeSlide = (newIndex) => {
    if (newIndex === currentIndex || isTransitioning) return;

    setIsTransitioning(true);

    setTimeout(() => {
      setCurrentIndex(newIndex);
      requestAnimationFrame(() => { setIsTransitioning(false); });
    }, 350);
  };

  if (!blogs.length) return null;

  const article = blogs[currentIndex];

  return (
    <section className="mb-8 w-full">
      <article className="relative grid w-full overflow-hidden border border-[#2A2A2A] bg-[#0A0A0A] md:grid-cols-[1.2fr_1fr]">
        {article?.imageUrl && (
          <div className={`relative h-[280px] w-full border-b-2 border-[#D0182B] bg-[#0A0A0A] transitionAll md:h-[480px] md:border-b-0
          ${isTransitioning ? "translate-x-1  opacity-0" : "translate-x-0  opacity-100"}`}>
            <img src={article?.imageUrl} alt={article.title} className="absolute inset-0 h-full w-full object-cover object-top"/>
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent md:bg-gradient-to-r" />
          </div>
        )}

        <div className={`relative z-20 flex w-full flex-col justify-center p-4 md:max-w-[88%] md:p-10 transitionAll
          ${isTransitioning ? "translate-x-1 opacity-0" : "translate-x-0 opacity-100"}`}>
          <div className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.1em] text-[#D0182B]">
            <span>{article?.publisher}</span>

            {article?.publishedAt && (
              <>
                <span className="h-3 w-[2px] bg-[#2A2A2A]" />
                <span className="text-[#8C8880]">
                  {formatDate(article?.publishedAt)}
                </span>
              </>
            )}
          </div>

          <h2 style={{ fontFamily: DISPLAY_FONT }} className="mb-4 text-3xl uppercase leading-[0.95] tracking-tight text-[#EDEAE1] md:text-4xl">
            {article?.title}
          </h2>

          <p className="mb-8 line-clamp-3 text-[15px] leading-relaxed text-[#8C8880]">
            {article?.excerpt}
          </p>

          <div className="mt-auto flex items-center justify-between gap-4">
            <span className="text-[#8C8880] text-sm font-medium">
              {article?.author}
            </span>

            <Link to={`/blog/${article?.slug}`} style={{ fontFamily: DISPLAY_FONT }} 
            className="border-2 border-[#D0182B] bg-[#D0182B] px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-[#EDEAE1] transitionAll hover:bg-transparent hover:text-[#D0182B]">
              Read Full Article
            </Link>
          </div>
        </div>
      </article>

      {blogs.length > 1 && (
        <div className="mt-4 flex w-full items-center justify-center gap-1">
          {blogs.map((blog, index) => (
            <button
              key={blog.id || index}
              type="button"
              onClick={() => changeSlide(index)}
              aria-label={`Go to article ${index + 1}`}
              className="group cursor-pointer p-1"
            >
              <div
                className={`h-1.5 transitionAll ${
                  index === currentIndex
                    ? "w-8 bg-[#D0182B]"
                    : "w-5 bg-[#2A2A2A] group-hover:bg-[#8C8880]"
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

export default FeaturedSlider;
